import React, { useEffect, useState } from 'react';
import { Check, History, Search } from 'lucide-react';
import { Button, Card } from '../../components/UI';
import { dailyTodosApi } from '../../api';

export default function TaskHistory({ revision }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [query, setQuery] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [limit, setLimit] = useState(30);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    dailyTodosApi.getHistory('all').then(res => {
      if (active) setGroups(res.data);
    }).catch(() => {
      if (active) setError('Could not load task history. Your records have not been changed.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [revision, retry]);

  useEffect(() => { setLimit(30); }, [query, from, to]);

  const filtered = groups.filter(group => (!from || group.date >= from) && (!to || group.date <= to))
    .map(group => ({ ...group, todos: group.todos.filter(todo =>
      `${todo.title} ${todo.description || ''}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())) }))
    .filter(group => group.todos.length > 0);
  const total = filtered.reduce((sum, group) => sum + group.todos.length, 0);
  const allTotal = groups.reduce((sum, group) => sum + group.todos.length, 0);
  let remaining = limit;
  const visible = filtered.map(group => {
    const todos = group.todos.slice(0, Math.max(remaining, 0));
    remaining -= todos.length;
    return { ...group, todos };
  }).filter(group => group.todos.length > 0);
  const formatDay = date => new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', weekday: 'short'
  });

  return (
    <section className="task-history" aria-label="Completed task history">
      <div className="history-heading">
        <div>
          <h2><History size={22} /> Completed tasks</h2>
          <p>Sorted by when you finished. Late tasks appear on their completion date.</p>
        </div>
        {!loading && !error && <span className="history-total">{allTotal} completed</span>}
      </div>
      <Card className="history-filters">
        <label className="history-search"><span><Search size={15} /> Search tasks</span>
          <input type="search" placeholder="Find a completed task…" value={query} onChange={e => setQuery(e.target.value)} />
        </label>
        <label><span>Completed from</span><input type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
        <label><span>Completed to</span><input type="date" value={to} min={from || undefined} onChange={e => setTo(e.target.value)} /></label>
        {(query || from || to) && <Button variant="secondary" onClick={() => { setQuery(''); setFrom(''); setTo(''); }}>Clear</Button>}
      </Card>
      <p className="history-timezone">Completion times: {Intl.DateTimeFormat().resolvedOptions().timeZone}</p>
      {from && to && from > to && <p role="alert">The end date must be on or after the start date.</p>}
      {loading ? <p role="status">Loading history…</p> : error ? (
        <Card><p role="alert">{error}</p><Button onClick={() => setRetry(value => value + 1)}>Try again</Button></Card>
      ) : total === 0 ? (
        <Card className="history-empty"><History size={32} /><h3>{allTotal ? 'No matching tasks' : 'No completed tasks yet'}</h3>
          <p>{allTotal ? 'Try another search or date range.' : 'Tasks you complete will stay here, including tasks from earlier days.'}</p>
        </Card>
      ) : <>
        <p className="history-result-count" role="status">{total} {total === 1 ? 'completion' : 'completions'}{query || from || to ? ' found' : ' in your history'}</p>
        {visible.map(group => (
          <section className="history-day" key={group.date}>
            <h3>{formatDay(group.date)}</h3>
            <div className="history-entries">
              {group.todos.map(todo => (
                <Card key={todo.completion_id} className="history-entry">
                  <span className="history-check"><Check size={18} /></span>
                  <div className="history-entry-body">
                    <h4>{todo.title}</h4>
                    {todo.description && <p className="history-description">{todo.description}</p>}
                    <div className="history-meta">
                      <span>{todo.type === 'recurring' ? 'Recurring task' : todo.type === 'unknown' ? 'Task' : 'One-time task'}</span>
                      {todo.scheduled_date !== group.date && <span>Originally scheduled {formatDay(todo.scheduled_date)}</span>}
                      {todo.task_deleted && <span>{todo.details_unavailable ? 'Original task details unavailable' : 'Original task deleted · completion kept'}</span>}
                    </div>
                  </div>
                  <time dateTime={todo.completed_at || undefined}>{todo.completed_at
                    ? new Date(todo.completed_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
                    : 'Time unavailable'}</time>
                </Card>
              ))}
            </div>
          </section>
        ))}
        {total > limit && <Button variant="secondary" onClick={() => setLimit(value => value + 30)}>Show more ({total - limit} remaining)</Button>}
      </>}
    </section>
  );
}
